import {
  Box,
  Checkbox,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from 'app/components/Icons/CloseIcon';
import InfoIcon from 'app/components/Icons/InfoIcon';
import SearchIcon from 'app/components/Icons/SearchIcon';
import { green, grey, grey10 } from 'app/components/ThemeProvider/colors';
import { colors, themePalette } from 'app/components/ThemeProvider/themeColors';

import { ExistingSalaryCard } from './model/types';

const CARD_STATUS_DISPLAY: Record<string, { labelKey: string; color: string }> = {
  ACTIVE: { labelKey: 'cardExtension.table.status.expiringSoon', color: '#FF9E1F' },
  CLOSED: { labelKey: 'cardExtension.table.status.expired', color: '#FF5F57' },
};

const getStatusDisplay = (status: string) =>
  CARD_STATUS_DISPLAY[status.toUpperCase()] ?? { labelKey: status, color: '#9E9E9E' };

const CardStatusCell = ({ status }: { status: string }) => {
  const { t } = useTranslation('salary-card');
  const { labelKey, color } = getStatusDisplay(status);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Typography fontSize={14} fontWeight={600}>
        {t(labelKey)}
      </Typography>
    </Box>
  );
};

interface CardsTableProps {
  cards: ExistingSalaryCard[];
  selectedIds: Set<number>;
  onSelectionChange: (selectedIds: Set<number>) => void;
  search: string;
  onSearchChange: (value: string) => void;
  isFetching?: boolean;
}

const CardsTable = ({
  cards,
  selectedIds,
  onSelectionChange,
  search,
  onSearchChange,
  isFetching = false,
}: CardsTableProps) => {
  const { t } = useTranslation('salary-card');
  const columns = [
    { id: 'fullName', label: t('cardExtension.table.columns.fullName') },
    { id: 'accountNumber', label: t('cardExtension.table.columns.account') },
    { id: 'expiryDate', label: t('cardExtension.table.columns.expiryDate') },
    { id: 'status', label: t('cardExtension.table.columns.status') },
  ];

  const isAllSelected =
    cards.length > 0 &&
    selectedIds.size === cards.length &&
    cards.every(card => selectedIds.has(card.id));

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    const next = new Set(selectedIds);

    if (event.target.checked) {
      cards.forEach(card => next.add(card.id));
    } else {
      cards.forEach(card => next.delete(card.id));
    }

    onSelectionChange(next);
  };

  const handleSelect = (event: ChangeEvent<HTMLInputElement>, id: number) => {
    const next = new Set(selectedIds);

    if (event.target.checked) {
      next.add(id);
    } else {
      next.delete(id);
    }

    onSelectionChange(next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          width: '100%',
          padding: '12px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          backgroundColor: colors.info[50],
          border: `1px solid ${colors.info[300]}`,
          borderRadius: '8px',
        }}
      >
        <InfoIcon sx={{ width: '24px', height: '24px', flexShrink: 0 }} color='secondary' />
        <Typography fontSize={16} fontWeight={400} lineHeight='24px'>
          {t('cardExtension.table.info.prefix')}{' '}
          <Typography component='span' fontSize={16} fontWeight={700} lineHeight='24px'>
            {t('cardExtension.table.info.highlight')}
          </Typography>{' '}
          {t('cardExtension.table.info.suffix')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <OutlinedInput
          value={search}
          onChange={event => onSearchChange(event.target.value)}
          placeholder={t('cardExtension.table.searchPlaceholder')}
          startAdornment={
            <InputAdornment position='start'>
              <SearchIcon sx={{ width: '22px', height: '22px' }} />
            </InputAdornment>
          }
          endAdornment={
            search ? (
              <InputAdornment
                position='end'
                sx={{ cursor: 'pointer' }}
                onClick={() => onSearchChange('')}
              >
                <CloseIcon />
              </InputAdornment>
            ) : null
          }
          sx={{
            width: '387px',
            maxWidth: '100%',
            height: '40px',
            backgroundColor: 'rgba(20, 25, 31, 0.06)',
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiOutlinedInput-input': {
              p: '14px 8px 14px 4px',
              fontSize: '15px',
              lineHeight: '22px',
            },
          }}
        />
      </Box>

      <TableContainer
        sx={{
          backgroundColor: 'white',
          boxShadow: '0px 0px 32px 0px rgba(0, 0, 0, 0.06)',
          borderRadius: '12px',
          minHeight: '400px',
          position: 'relative',
        }}
      >
        {isFetching && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              zIndex: 1,
              borderRadius: '12px',
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}

        <Table>
          <TableHead>
            <TableRow sx={{ border: 'none' }}>
              <TableCell sx={{ border: 'none', p: '16px 8px 12px 24px', width: 48 }}>
                <Checkbox
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={cards.length === 0}
                  sx={{
                    color: 'rgba(20, 25, 31, 0.22)',
                    p: 0,
                    '&.Mui-checked': { color: green },
                  }}
                />
              </TableCell>
              {columns.map(column => (
                <TableCell key={column.id} sx={{ border: 'none', p: '16px 8px 12px' }}>
                  <Typography fontSize={13} fontWeight={500} color={grey}>
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {cards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} sx={{ border: 'none', py: 8 }}>
                  <Typography
                    textAlign='center'
                    fontSize={14}
                    color={themePalette.text_and_icon.third_party}
                  >
                    {t('cardExtension.table.notFound')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              cards.map((card, index) => (
                <TableRow
                  key={card.id}
                  sx={{
                    border: 'none',
                    backgroundColor: index % 2 !== 0 ? grey10 : 'transparent',
                  }}
                >
                  <TableCell sx={{ border: 'none', p: '14px 8px 14px 24px' }}>
                    <Checkbox
                      checked={selectedIds.has(card.id)}
                      onChange={event => handleSelect(event, card.id)}
                      sx={{
                        color: 'rgba(20, 25, 31, 0.22)',
                        p: 0,
                        '&.Mui-checked': { color: green },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 'none',
                      p: '14px 8px',
                      minWidth: '150px',
                      alignContent: 'baseline',
                    }}
                  >
                    <Typography fontSize={14} fontWeight={600}>
                      {card.fullName}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 'none',
                      p: '14px 8px',
                      minWidth: '150px',
                      alignContent: 'baseline',
                    }}
                  >
                    <Typography fontSize={14} fontWeight={600}>
                      {card.accountNumber}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 'none',
                      p: '14px 8px',
                      minWidth: '150px',
                      alignContent: 'baseline',
                    }}
                  >
                    <Typography fontSize={14} fontWeight={600}>
                      {card.expiryDate}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      border: 'none',
                      p: '14px 8px',
                      minWidth: '150px',
                      alignContent: 'baseline',
                    }}
                  >
                    <CardStatusCell status={card.status} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CardsTable;
